import { IThemePack } from '@/shared/interfaces/IThemePack';
import { ref, inject, type InjectionKey, readonly, computed } from 'vue';

const defaultThemePack: IThemePack = {
  name: 'defaultThemes',
  vuetify: {
    defaults: {
      VTextField: {
        variant: 'filled',
        singleLine: false,
        hideDetails: true,
      },
      VSelect: {
        variant: 'filled',
        singleLine: false,
        hideDetails: true,
      },
    },
    theme: {
      dark: false,
      colors: {
        background: '#a6a5a9',
        error: '#BF4342',
      },
    },
  },
};

export type IThemePackProvider = ReturnType<typeof hCreateThemePack>;

export const themePackKey: InjectionKey<IThemePackProvider> = Symbol('Helix Theme Pack');

// Use Inject and Provide pattern for this
/**
 * A Vue composable that provides theme pack management functionalities.
 *
 * @function
 * @name hCreateThemePack
 * @description
 * This composable provides methods to manage theme packs within a Vue application.
 * It includes functionalities to set a new theme pack and revert to the previously selected theme pack.
 *
 * @param {Record<string, IThemePack>} themePacksDict - A dictionary of available theme packs.
 * @param {string} defaultThemePackName - The name of the default theme pack.
 * @returns {Object} An object containing the following properties and methods:
 * - `hThemePack`: A readonly reactive reference to the current theme pack.
 * - `hThemePacksDict`: A readonly reference to the dictionary of theme packs.
 * - `hSetThemePack`: A function to set a new theme pack.
 * - `hRevertThemePack`: A function to revert to the previously selected theme pack.
 */
export function hCreateThemePack(themePacksDict: Record<string, IThemePack>, defaultThemePackName: string) {
  const resolvePack = (name: string) =>
    (Object.hasOwn(themePacksDict, name) ? themePacksDict[name] : undefined) ??
    (Object.hasOwn(themePacksDict, defaultThemePackName) ? themePacksDict[defaultThemePackName] : undefined) ??
    defaultThemePack;

  const themePack = ref<IThemePack>(resolvePack(defaultThemePackName));
  const selectedThemePackName = ref(defaultThemePackName);

  function hSetThemePack(newThemePackName: string, options: { temporary: boolean } = { temporary: false }) {
    // The `temporary` flag enables temporarily changing the theme in certain scenarios (i.e: page themes) and easily reverting to the previously selected theme
    if (!options.temporary) {
      selectedThemePackName.value = newThemePackName;
    }

    themePack.value = resolvePack(newThemePackName);
  }

  function hGetThemePack(themePackName: string) {
    return resolvePack(themePackName);
  }

  function hRevertThemePack() {
    hSetThemePack(selectedThemePackName.value ?? defaultThemePackName);
  }

  return {
    hThemePack: readonly(themePack),
    hThemePacksDict: readonly(themePacksDict),
    hSetThemePack,
    hGetThemePack,
    hRevertThemePack,
  };
}

/**
 * A Vue composable that provides access to the Helix Themepack.
 *
 * @function
 * @name useHThemePack
 * @description
 * This composable injects the Helix Themepack provider and returns it. If the provider is not found, it throws an error.
 *
 * @throws {Error} Throws an error if the Helix Themepack provider is not found.
 *
 * @returns {Object} The injected Helix Themepack provider.
 */
export function useHThemePack() {
  const providedThemePack = inject(themePackKey);

  if (!providedThemePack) {
    throw new Error('Was not able to inject Helix Themepack provider');
  }

  return providedThemePack;
}

function getHelixAppDefaults(defaults: Record<string, any> = {}) {
  return defaults;
}

export function useHThemePackDefaults() {
  const { hThemePack } = useHThemePack();

  const defaults = computed(() => getHelixAppDefaults(hThemePack.value?.vuetify?.defaults));

  return { defaults };
}
